import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import CustomerLayout from "@/components/layouts/CustomerLayout";
import DriverLayout from "@/components/layouts/DriverLayout";
import AdminLayout from "@/components/layouts/AdminLayout";
import CustomerHome from "@/pages/customer/Home";
import ProductDetail from "@/pages/customer/ProductDetail";
import Cart from "@/pages/customer/Cart";
import Checkout from "@/pages/customer/Checkout";
import PaymentSuccess from "@/pages/customer/PaymentSuccess";
import PaymentCancel from "@/pages/customer/PaymentCancel";
import QRPayment from "@/pages/customer/QRPayment";
import OrderTracking from "@/pages/customer/OrderTracking";
import OrdersHistory from "@/pages/customer/OrdersHistory";
import Profile from "@/pages/customer/Profile";
import DriverDashboard from "@/pages/driver/Dashboard";
import DriverActiveDelivery from "@/pages/driver/ActiveDelivery";
import DriverEarnings from "@/pages/driver/Earnings";
import AdminOrders from "@/pages/admin/Orders";
import AdminProducts from "@/pages/admin/Products";
import AdminAnalytics from "@/pages/admin/Analytics";

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
        <div className="w-8 h-8 border-2 border-[var(--red)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace state={{ from: location.pathname }} />;
  if (allow && !allow.includes(user.role)) {
    if (user.role === "driver") return <Navigate to="/driver" replace />;
    if (user.role === "store_admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
        <div className="w-8 h-8 border-2 border-[var(--red)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Landing />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  if (user.role === "store_admin") return <Navigate to="/admin" replace />;
  return (
    <CustomerLayout>
      <CustomerHome />
    </CustomerLayout>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/products/:productId" element={<Protected allow={["customer"]}><CustomerLayout><ProductDetail /></CustomerLayout></Protected>} />
      <Route path="/cart" element={<Protected allow={["customer"]}><CustomerLayout><Cart /></CustomerLayout></Protected>} />
      <Route path="/checkout" element={<Protected allow={["customer"]}><CustomerLayout><Checkout /></CustomerLayout></Protected>} />
      <Route path="/payment/success" element={<Protected allow={["customer"]}><CustomerLayout><PaymentSuccess /></CustomerLayout></Protected>} />
      <Route path="/payment/cancel" element={<Protected allow={["customer"]}><CustomerLayout><PaymentCancel /></CustomerLayout></Protected>} />
      <Route path="/pay/qr/:orderId" element={<Protected allow={["customer"]}><CustomerLayout><QRPayment /></CustomerLayout></Protected>} />
      <Route path="/orders" element={<Protected allow={["customer"]}><CustomerLayout><OrdersHistory /></CustomerLayout></Protected>} />
      <Route path="/orders/:orderId" element={<Protected allow={["customer"]}><CustomerLayout><OrderTracking /></CustomerLayout></Protected>} />
      <Route path="/profile" element={<Protected allow={["customer", "driver", "store_admin"]}><CustomerLayout><Profile /></CustomerLayout></Protected>} />

      <Route path="/driver" element={<Protected allow={["driver"]}><DriverLayout><DriverDashboard /></DriverLayout></Protected>} />
      <Route path="/driver/orders/:orderId" element={<Protected allow={["driver"]}><DriverLayout><DriverActiveDelivery /></DriverLayout></Protected>} />
      <Route path="/driver/earnings" element={<Protected allow={["driver"]}><DriverLayout><DriverEarnings /></DriverLayout></Protected>} />

      <Route path="/admin" element={<Protected allow={["store_admin"]}><AdminLayout><AdminOrders /></AdminLayout></Protected>} />
      <Route path="/admin/products" element={<Protected allow={["store_admin"]}><AdminLayout><AdminProducts /></AdminLayout></Protected>} />
      <Route path="/admin/analytics" element={<Protected allow={["store_admin"]}><AdminLayout><AdminAnalytics /></AdminLayout></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
